import React, { useState, useRef, useCallback } from 'react';
import { SETTING } from 'interfaces/settings';
import { getRangeConfig } from './rangeConfig';
import { useAutodialerSettings } from '../../hooks/useAutodialerSettings';
import { Typography } from 'interfaces/typography';

export function RangeSettingControl({ setting }: { setting: SETTING }) {
    const { getValueForSetting, updateSetting } = useAutodialerSettings();
    const config = getRangeConfig(setting);
    const value = getValueForSetting(setting);
    const currentValue = typeof value === 'number' ? value : config.defaultValue;
    
    const [isDragging, setIsDragging] = useState(false);
    const sliderRef = useRef<HTMLDivElement>(null);
    
    const percentage = ((currentValue - config.min) / (config.max - config.min)) * 100;
    
    const onChange = useCallback((newValue: number) => {
        const clampedValue = Math.max(config.min, Math.min(config.max, newValue));
        const steppedValue = Math.round(clampedValue / config.step) * config.step;
        updateSetting(setting, steppedValue);
    }, [setting, updateSetting, config]);
    
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        updateValueFromEvent(e);
    }, []);
    
    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) {
            updateValueFromEvent(e);
        }
    }, [isDragging]);
    
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);
    
    const updateValueFromEvent = useCallback((e: React.MouseEvent | MouseEvent) => {
        if (!sliderRef.current) return;
        
        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const newValue = config.min + percentage * (config.max - config.min);
        
        onChange(newValue);
    }, [config, onChange]);
    
    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);
    
    return (
        <div className="flex items-center gap-4 flex-1 h-full">
            {/* Custom Slider */}
            <div className="flex-1 relative px-1">
                <div
                    ref={sliderRef}
                    className="w-full h-1.5 bg-gray-200 rounded-full cursor-pointer relative group"
                    onMouseDown={handleMouseDown}
                >
                    {/* Track fill */}
                    <div
                        className="absolute top-0 left-0 h-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-200"
                        style={{ width: `${percentage}%` }}
                    />
                    
                    {/* Thumb */}
                    <div
                        className={`absolute top-1/2 w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow-lg transform -translate-y-1/2 transition-all duration-200 ${
                            isDragging ? 'scale-125 shadow-xl border-blue-600' : 'hover:scale-110 hover:shadow-lg'
                        }`}
                        style={{ 
                            left: `${percentage}%`,
                            marginLeft: '-8px',
                            cursor: isDragging ? 'grabbing' : 'grab'
                        }}
                    />
                </div>
            </div>
            
            {/* Value display and input */}
            <div className="flex items-center gap-2 min-w-0">
                <input
                    type="number"
                    min={config.min}
                    max={config.max}
                    step={config.step}
                    value={currentValue}
                    onChange={(e) => {
                        const newValue = parseFloat(e.target.value);
                        if (!isNaN(newValue)) {
                            onChange(newValue);
                        }
                    }}
                    className="w-14 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-center bg-white shadow-sm"
                    style={{
                        fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                        fontSize: '10px',
                        fontWeight: 500,
                        color: '#374151',
                        lineHeight: '15px',
                        letterSpacing: '0.02em'
                    }}
                />
                {config.unit && (
                    <Typography variant="smallParagraph" color="#4b5563" fontWeight="500" className="whitespace-nowrap">
                        {config.unit}
                    </Typography>
                )}
            </div>
        </div>
    );
} 